import { createClient } from '@supabase/supabase-js';
import { useState, useEffect } from 'react';

// Leer variables de entorno
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_KEY;

// Validar que las variables de entorno estén definidas
if (!SUPABASE_URL || !SUPABASE_KEY) {
  throw new Error('Las variables de entorno VITE_SUPABASE_URL y VITE_SUPABASE_KEY deben estar definidas en el archivo .env');
}

// Create a singleton instance of the Supabase client with improved configuration
let supabaseClient;

try {
  supabaseClient = createClient(SUPABASE_URL, SUPABASE_KEY, {
    realtime: {
      params: {
        eventsPerSecond: 10
      },
      timeout: 30000,
      heartbeat: {
        interval: 3000,
        maxRetries: 10
      }
    },
    auth: {
      autoRefreshToken: true,
      persistSession: true
    },
    db: {
      schema: 'public'
    }
  });
} catch (error) {
  console.error('Error initializing Supabase client:', error);
  // Create a fallback client with minimal configuration
  supabaseClient = createClient(SUPABASE_URL, SUPABASE_KEY);
}

const useSupabaseClient = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [error, setError] = useState(null);
  const maxRetries = 5;

  useEffect(() => {
    let reconnectTimeout;
    let mounted = true;
    let connectionTestInterval;

    const testConnection = async () => {
      if (!mounted) return;

      try {
        const { data, error } = await supabaseClient
          .from('invoices')
          .select('count')
          .limit(1);

        if (error) throw error;

        if (mounted) {
          setError(null);
          setIsConnected(true);
        }
      } catch (err) {
        if (mounted) {
          setIsConnected(false);
          setError(`Failed to connect to Supabase: ${err.message}`);
        }
      }
    };

    const setupChannel = () => {
      if (!mounted) return;

      // Initial connection test
      testConnection();

      // Set up periodic connection testing
      connectionTestInterval = setInterval(testConnection, 30000);

      const channel = supabaseClient.channel('system')
        .on('system', { event: '*' }, (payload) => {
          if (!mounted) return;
          
          if (payload.event === 'connected') {
            setIsConnected(true);
            setRetryCount(0);
            setError(null);
          } else if (payload.event === 'disconnected') {
            setIsConnected(false);
          }
        })
        .subscribe((status) => {
          if (!mounted) return;

          const isSubscribed = status === 'SUBSCRIBED';
          
          if (!isSubscribed && retryCount < maxRetries) {
            clearTimeout(reconnectTimeout);
            reconnectTimeout = setTimeout(() => {
              if (!mounted) return;
              
              try {
                setRetryCount(prev => prev + 1);
                channel.unsubscribe();
                setupChannel();
              } catch (error) {
                console.error('Error during reconnection attempt:', error);
              }
            }, Math.min(1000 * Math.pow(2, retryCount), 30000));
          }
        });

      return () => {
        channel.unsubscribe();
        clearInterval(connectionTestInterval);
      };
    };

    const cleanup = setupChannel();
    
    return () => {
      mounted = false;
      clearTimeout(reconnectTimeout);
      clearInterval(connectionTestInterval);
      if (cleanup) cleanup();
    };
  }, [retryCount]);

  return {
    supabase: supabaseClient,
    isConnected,
    error,
    retryCount
  };
};

export default useSupabaseClient;